// src/pages/SenhaForm.jsx
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AppLayout from '../components/AppLayout';
import {
  Box, Button, Grid, Paper, TextField, Typography, Alert,
  FormGroup, FormControlLabel, Checkbox
} from '@mui/material';
import api from '../api/client';

import { toBRDate, toBRDateTime } from '../utils/date';


// util: dígito (0-9)
const isDigit = (ch) => /^[0-9]$/.test(ch);

// input de 1 caractere com foco automático
function DigitBox({ label, value, inputRef, onChange, onKeyDown, onPaste }) {
  return (
    <TextField
      label={label}
      value={value}
      inputRef={inputRef}
      onChange={onChange}
      onKeyDown={onKeyDown}
      onPaste={onPaste}
      type="text"
      size="small"
      inputProps={{
        maxLength: 1,
        inputMode: 'numeric',
        pattern: '[0-9]',
        style: { textAlign: 'center', fontWeight: 600 },
      }}
      sx={{ width: 64 }}
    />
  );
}

// tenta decodificar base64; se falhar, devolve o original
function tryDecodeBase64(raw) {
  if (!raw) return '';
  const s = raw.startsWith('base64:') ? raw.slice(7) : raw;
  const cleaned = s.replace(/\s+/g, '');
  const looksB64 = /^[A-Za-z0-9+/=]+$/.test(cleaned) && cleaned.length % 4 === 0;
  if (!looksB64) return raw;
  try {
    return atob(cleaned);
  } catch {
    return raw;
  }
}

export default function SenhaForm() {
  const { id } = useParams(); // 'novo' ou :id
  const navigate = useNavigate();
  const isEdit = id && id !== 'novo';

  const [form, setForm] = useState({
    // id_senha: ← NÃO usar em criação (auto-increment)
    cat_senha: ['a'],
    apelido_senha: '',
    nome_senha: '',
    adm_login: '',
    pasw_senha: '',            // em edição abrimos automaticamente (b64 → texto)
    email_senha: '',
    webm_senha: 'a',
    data_senha: '',
    visita_senha: 0,           // somente leitura
    decode_count: 0,           // somente leitura
    cod_proprietario: '',
    datemodify: null,
    timemodify: null,
  });
  const [error, setError] = useState(null);

  // Código Proprietário visual (4 caixas)
  const [CP, setCP] = useState(['', '', '', '']);
  const cpRefs = useRef(Array.from({ length: 4 }, () => React.createRef()));

  const setField = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  const toggleCat = (value) => {
    setForm((prev) => {
      const set = new Set(prev.cat_senha || []);
      if (set.has(value)) set.delete(value);
      else set.add(value);
      return { ...prev, cat_senha: Array.from(set) };
    });
  };

  const syncCPtoForm = (arr) => {
    const joined = arr.join('');
    setForm((prev) => ({ ...prev, cod_proprietario: joined.length ? joined : '' }));
  };
  const focusNext = (i) => { if (i < 3) cpRefs.current[i + 1].current?.focus(); };
  const focusPrev = (i) => { if (i > 0) cpRefs.current[i - 1].current?.focus(); };

  const onCPChange = (i) => (e) => {
    const raw = e.target.value.slice(-1);
    if (!raw) {
      setCP((prev) => {
        const arr = prev.map((v, idx) => (idx === i ? '' : v));
        syncCPtoForm(arr);
        return arr;
      });
      return;
    }
    if (!isDigit(raw)) return;
    setCP((prev) => {
      const arr = prev.map((v, idx) => (idx === i ? raw : v));
      syncCPtoForm(arr);
      return arr;
    });
    focusNext(i);
  };
  const onCPKeyDown = (i) => (e) => {
    if (e.key === 'Backspace' && CP[i] === '') {
      e.preventDefault();
      focusPrev(i);
      setCP((prev) => {
        const idx = Math.max(0, i - 1);
        const arr = prev.map((v, j) => (j === idx ? '' : v));
        syncCPtoForm(arr);
        return arr;
      });
    }
    if (e.key === 'ArrowLeft') { e.preventDefault(); focusPrev(i); }
    if (e.key === 'ArrowRight') { e.preventDefault(); focusNext(i); }
  };
  const onCPPaste = (i) => (e) => {
    const text = (e.clipboardData || window.clipboardData).getData('text') || '';
    if (!text) return;
    e.preventDefault();
    const chars = text.split('').filter(isDigit);
    if (!chars.length) return;
    setCP((prev) => {
      const arr = [...prev];
      let idx = i;
      for (const ch of chars) {
        if (idx > 3) break;
        arr[idx++] = ch;
      }
      syncCPtoForm(arr);
      return arr;
    });
    const nextIdx = Math.min(3, i + chars.length);
    cpRefs.current[nextIdx]?.current?.focus();
  };

  // Carregar registro quando edição (e abrir senha + preencher CP se existir)
useEffect(() => {
  let cancelled = false;
  (async () => {
    if (!isEdit) return;
    try {
      const { data } = await api.get(`/api/api/senhas/${id}`);
      const openedPassword = data.pasw_senha ;

      const cp = (data.cod_proprietario || '').toString().replace(/\D/g, '').slice(0, 4);
      const cpArray = [cp[0] || '', cp[1] || '', cp[2] || '', cp[3] || ''];

      if (cancelled) return;
      setForm(prev => ({
        ...prev,
        ...data,
        cat_senha: Array.isArray(data.cat_senha)
          ? data.cat_senha
          : (data.cat_senha ? String(data.cat_senha).split(',') : []),
        pasw_senha: openedPassword,
        cod_proprietario: cp,
      }));
      setCP(cpArray);
    } catch {
      if (!cancelled) setError('Falha ao carregar registro');
    }
  })();
  return () => { cancelled = true; };
}, [id, isEdit]); // ← dependências corretas


  const save = async (e) => {
    e.preventDefault();
    setError(null);
    try {
      // remove campos somente-leitura e id_senha do payload
      const {
        visita_senha, decode_count, datemodify, timemodify, id_senha, ...payload
      } = form;

      if (isEdit) {
        await api.put(`/api/api/senhas/${id}`, payload);
      } else {
        await api.post('/api/api/senhas', payload); // sem id_senha (auto-increment)
      }
      navigate('/senhas');
    } catch (err) {
      const msg = err?.response?.data?.message || 'Erro ao salvar';
      setError(msg);
    }
  };

  return (
    <AppLayout>
      <Typography variant="h5" fontWeight={700} gutterBottom>
        {isEdit ? 'Alterar' : 'Cadastrar'}
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper sx={{ p: 3 }} component="form" onSubmit={save}>
        <Grid container spacing={2}>

          {/* Categoria (checkbox) */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ mb: .5 }}>Categoria</Typography>
            <FormGroup row>
              <FormControlLabel
                control={<Checkbox checked={form.cat_senha?.includes('a') || false} onChange={() => toggleCat('a')} />}
                label="Administrador"
              />
              <FormControlLabel
                control={<Checkbox checked={form.cat_senha?.includes('m') || false} onChange={() => toggleCat('m')} />}
                label="Master"
              />
            </FormGroup>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField required fullWidth label="Apelido"
              value={form.apelido_senha}
              onChange={(e) => setField('apelido_senha', e.target.value)}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField required fullWidth label="Nome"
              value={form.nome_senha}
              onChange={(e) => setField('nome_senha', e.target.value)}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField required fullWidth label="Login"
              value={form.adm_login}
              onChange={(e) => setField('adm_login', e.target.value)}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              required
              fullWidth
              label="Senha"
              value={form.pasw_senha}
              onChange={(e) => setField('pasw_senha', e.target.value)}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField required fullWidth type="email" label="Email"
              value={form.email_senha}
              onChange={(e) => setField('email_senha', e.target.value)}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth type="date"
              label="Data Cadastro"
              InputLabelProps={{ shrink: true }}
              value={form.data_senha || ''}
              onChange={(e) => setField('data_senha', e.target.value)}
            />
          </Grid>

          {/* Somente visualização */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth label="Visitas"
              value={form.visita_senha ?? 0}
              InputProps={{ readOnly: true }}
              disabled
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth label="Solicitações Gerar Contra Senha"
              value={form.decode_count ?? 0}
              InputProps={{ readOnly: true }}
              disabled
            />
          </Grid>

          {/* Código Proprietário: 4 dígitos */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Código Proprietário</Typography>
            <Grid container spacing={2} alignItems="center">
              {CP.map((v, i) => (
                <Grid item key={`cp-${i}`}>
                  <DigitBox
                    value={v}
                    inputRef={cpRefs.current[i]}
                    onChange={onCPChange(i)}
                    onKeyDown={onCPKeyDown(i)}
                    onPaste={onCPPaste(i)}
                  />
                </Grid>
              ))}
            </Grid>
          </Grid>



          {isEdit && (
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">
                Última modificação: {form.datemodify ? toBRDateTime(`${form.datemodify} ${form.timemodify||'00:00:00'}`) : '-'}
              </Typography>
            </Grid>
          )}
        </Grid>

        <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
          <Button type="submit" variant="contained">Salvar</Button>
          <Button variant="outlined" onClick={() => navigate('/senhas')}>Cancelar</Button>
        </Box>
      </Paper>
    </AppLayout>
  );
}
